import { ensureOrgEmployees, ensureGeofences } from "../../../../../../services/useOrgData";
import React, { useState, useEffect, useMemo } from "react";
import { Avatar, Box, Stack } from '@mui/material';
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import TopBar from "../../../../../../component/TopBar";
import { useNavigate } from "react-router-dom";
import DataTable from "../../../../../../component/DataTable";
import { FilterSelect, StatusChip, PersonCell, SearchInput, personName } from "../../../../../../component/listing";
import TrendChart from "../../../../../../component/charts/TrendChart";
import { toneColors } from "../../../../../../component/statusTone";
import { getOrgShifts } from "../../../../../../services/globalData";

import { getAllDayAttendance } from "../../../../../../services/facialRecognition";
import { getAllAttendanceRequests } from "../../../../../../services/attendanceRequest";

import { isPointInFence } from "../../../../../../utils/isPointInFence";
import { useNotify } from "../../../../../../component/NotificationProvider";
import { tokens, displayFont, chartColors, motion, radius } from "../../../../../../theme";

import { pressable } from "../../../../../../component/pressableProps";
import { counterRowSx } from "../../../../container/frameLayout";
import {
  utcDayKey,
  dayLabel,
  rangeKeys,
  flagsOf,
  rowStatus,
  countBuckets,
  selectRows,
  punchAudit,
  toggleSeries,
  buildTrend,
  arrivalBand,
  trendCaption,
  clockLabel,
  hoursLabel,
} from "./dayTrend";

// group raw day records into [{ date, employees: [{ employeeId, records }] }]
const groupRecordsByDateAndEmployee = (records = []) => {
  const byDate = (records || []).reduce((acc, record) => {
    const d = utcDayKey(record?.date);
    if (!d) return acc;
    acc[d] = acc[d] || {};
    acc[d][record?.employee] = acc[d][record?.employee] || [];
    acc[d][record?.employee].push(record);
    return acc;
  }, {});
  return Object.entries(byDate).map(([date, emps]) => ({
    date,
    employees: Object.entries(emps).map(([employeeId, records]) => ({ employeeId, records })),
  }));
};

// The book's wording, not the schema's. "Regularise" is what the person waiting
// on you is asking for; REGULARIZATION is what the table stores it as.
const TYPE_LABEL = { REGULARIZATION: "Regularise", SHIFT_CHANGE: "Shift change" };
const initialsOf = (name) => (name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase();

const MISSING_PUNCH_COLOR = toneColors("violet").fg;

/**
 * The six counters.
 *
 * Each one is a filter for the table below rather than a decoration: clicking
 * Late selects the fifteen people. They share the `bucket` state with the chips
 * under the table, so the two controls address ONE filter instead of competing
 * for it — the counter lights up when the chip is clicked and vice versa.
 */
const COUNTERS = [
  // "Present today 124" selects those 124, not the whole roster of 142 — the
  // way back to everyone is the All chip, which is a different number.
  { bucket: "present", key: "present", label: "Present today", pastLabel: "Present", tone: tokens.accent },
  { bucket: "onTime", key: "onTime", label: "On time", tone: tokens.success },
  { bucket: "late", key: "late", label: "Late", tone: tokens.warning },
  { bucket: "missing", key: "missing", label: "Missing punch", tone: MISSING_PUNCH_COLOR },
  { bucket: "overtime", key: "overtime", label: "Overtime", tone: tokens.info },
  { bucket: "absent", key: "absent", label: "Absent", tone: tokens.danger },
];

// The chips repeat five of the six, because "All" is the state the table starts
// in and needs a way back to. Overtime is a counter only: it is an overlay on
// the arrival buckets rather than a status a row can be shown as.
const CHIPS = [
  { bucket: "all", key: null, label: "All" },
  { bucket: "onTime", key: "onTime", label: "On time" },
  { bucket: "late", key: "late", label: "Late" },
  { bucket: "missing", key: "missing", label: "Missing punch" },
  { bucket: "absent", key: "absent", label: "Absent" },
];

const SERIES_META = {
  // The categorical ramp, in order. A series is a category, not a status, so
  // these come from chartColors rather than the status palette — reordering
  // them would silently recolour every reader's mental map.
  attendance: { label: "Attendance", color: chartColors[0] },
  late: { label: "Late", color: chartColors[1] },
  // chartColors[5] is the violet that codes missing-punch in the register, the
  // month strip and the muster, so one meaning keeps one colour module-wide.
  missing: { label: "Missing punch", color: chartColors[5] },
};

const Dash = () => <Box component="span" sx={{ color: tokens.textInactive }}>—</Box>;

/** One counter in the top row. Pressed state is a state, not a hover. */
const Counter = ({ label, value, tone, active, onClick }) => (
  <Box
    {...pressable(onClick)}
    aria-pressed={active}
    sx={{
      flex: 1,
      minWidth: 0,
      background: tokens.card,
      border: `1px solid ${active ? tone : tokens.border}`,
      borderRadius: "10px",
      p: "1rem 1.125rem",
      cursor: "pointer",
      // A second inset ring rather than a thicker border, so the tile does not
      // move by a pixel when it becomes the selected one.
      boxShadow: active ? `inset 0 0 0 1px ${tone}` : "none",
      transition: `border-color ${motion.duration[1]}ms ${motion.easing.standard}, box-shadow ${motion.duration[1]}ms ${motion.easing.standard}`,
      "&:hover": { borderColor: tone },
      "&:focus-visible": { outline: `2px solid ${tokens.accent}`, outlineOffset: "2px" },
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: "0.5rem" }}>
      <Box sx={{ width: "7px", height: "7px", borderRadius: "50%", background: tone, flex: "none" }} />
      <Box sx={{ fontSize: "0.8125rem", fontWeight: active ? 600 : 500, color: active ? tokens.text : tokens.textMuted }}>
        {label}
      </Box>
    </Stack>
    <Box sx={{ fontFamily: displayFont, fontSize: "1.75rem", fontWeight: 700, color: tokens.text, fontVariantNumeric: "tabular-nums" }}>
      {value}
    </Box>
  </Box>
);

/** One row of the breakdown: the count and the share of the roster it is. */
const BreakdownBar = ({ label, value, total, tone }) => {
  const pct = total ? Math.round((value / total) * 100) : 0;
  return (
    <Box sx={{ mb: "1rem" }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: "0.4375rem" }}>
        <Box sx={{ fontSize: "0.8125rem", color: tokens.text }}>{label}</Box>
        <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
          {value} · {pct}%
        </Box>
      </Stack>
      {/* The fill is full-width and slid into place rather than resized, so the
          animation stays on the compositor. translateX (not scaleX) because
          scaling a 7px pill would stretch its radius into an oval; here the
          left end is simply clipped by the track's own rounding. */}
      <Box sx={{ height: "7px", borderRadius: "4px", background: tokens.border, overflow: "hidden" }}>
        <Box
          sx={{
            width: "100%",
            height: "100%",
            background: tone,
            borderRadius: "4px",
            transform: `translateX(${pct - 100}%)`,
            transition: `transform ${motion.duration[3]}ms ${motion.easing.standard}`,
          }}
        />
      </Box>
    </Box>
  );
};

const panelSx = { background: tokens.card, border: `1px solid ${tokens.border}`, borderRadius: `${radius.card}px`, p: "1.25rem" };
const panelTitleSx = { fontFamily: displayFont, fontSize: "1rem", fontWeight: 700, color: tokens.text };
const captionSx = { fontSize: "0.8125rem", color: tokens.textMuted };
const linkBtnSx = { fontSize: "0.8125rem", color: tokens.accent, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "0.25rem", "&:hover": { color: tokens.accentHover } };

const AttendanceDashboard = () => {
  const navigate = useNavigate();
  const notify = useNotify();

  const shiftOptions = [
    { value: "all", label: "All shifts" },
    ...(getOrgShifts()?.map((s) => ({ value: s?._id, label: s?.name })) || []),
  ];

  const todayKey = useMemo(() => utcDayKey(new Date()), []);
  const [selectedShift, setSelectedShift] = useState("all");
  const [selectedDay, setSelectedDay] = useState(todayKey);
  const [rangeDays, setRangeDays] = useState(30);
  // Two series at most; three crossing lines on a 180px plot is a picture of
  // nothing. Attendance and Late are the pair that answers "how many, and how
  // many of those were late" — Missing punch is opted into.
  const [visibleSeries, setVisibleSeries] = useState(["attendance", "late"]);
  const [bucket, setBucket] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  // Retry has to change something the fetch depends on, or nothing happens:
  // setting the day to the day it already is re-renders and re-fetches nothing.
  const [reloadKey, setReloadKey] = useState(0);

  const [allEmployees, setAllEmployees] = useState([]);
  const [grouped, setGrouped] = useState([]);
  const [requests, setRequests] = useState([]);
  const [fence, setFence] = useState();

  // Roster, pending requests and the geofence do not depend on the window being
  // charted, so they are fetched once rather than on every range change.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [employees, reqRes, fenceRes] = await Promise.all([
          ensureOrgEmployees(),
          getAllAttendanceRequests(),
          ensureGeofences().catch(() => null),
        ]);
        if (!active) return;
        setAllEmployees(Array.isArray(employees) ? employees : []);
        setRequests(Array.isArray(reqRes?.data) ? reqRes.data.filter((r) => r?.status === "Pending") : []);
        setFence(fenceRes?.data?.[0]?.geofenceObj?.fences?.[0]);
      } catch (err) {
        if (active) notify.error(err?.message || "Failed to load dashboard");
      }
    })();
    return () => { active = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Attendance follows the window actually charted. It used to fetch the org's
  // entire history once and slice it in the browser, which grows without bound
  // while the page still only ever draws `rangeDays` of it.
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setFailed(false);
      const window = rangeKeys(selectedDay, rangeDays);
      if (!window.length) { setLoading(false); return; }
      try {
        const attRes = await getAllDayAttendance({ startDate: window[0], endDate: window[window.length - 1] });
        if (!active) return;
        if (attRes?.success === false) throw new Error(attRes?.message || "Failed to load attendance");
        setGrouped(groupRecordsByDateAndEmployee(attRes?.data));
      } catch (err) {
        if (!active) return;
        setFailed(true);
        notify.error(err?.message || "Failed to load attendance");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [selectedDay, rangeDays, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const empById = useMemo(() => {
    const m = {};
    (allEmployees || []).forEach((e) => { m[e._id] = e; });
    return m;
  }, [allEmployees]);

  const totalActive = useMemo(
    () => (allEmployees || []).filter((e) => e?.isActive !== false).length,
    [allEmployees]
  );

  // Headcount ON a given day, so a historical percentage is scored against the
  // team that actually existed then rather than against today's. Degrades to the
  // current headcount when nobody has a joining date — which is reported in the
  // caption rather than hidden, because it makes older figures approximate.
  const headcountBasis = useMemo(() => {
    const active = (allEmployees || []).filter((e) => e?.isActive !== false);
    const dated = active.filter((e) => e?.joiningDate);
    return {
      // "Do we know this is exact?" has three answers, not two: null while the
      // roster has not loaded, so the caption can decline to claim either way.
      exact: active.length === 0 ? null : dated.length === active.length,
      total: active.length,
      on: (key) =>
        active.filter((e) => {
          const joined = utcDayKey(e?.joiningDate);
          const left = utcDayKey(e?.lastWorkingDay);
          if (joined && joined > key) return false;
          if (left && left < key) return false;
          return true;
        }).length,
    };
  }, [allEmployees]);

  const isToday = selectedDay === todayKey;

  // The roster for the selected day: everyone with a record, decorated with the
  // flags the counters and the status column both read, plus everyone active who
  // has no record at all. One list, so a counter and a chip cannot disagree.
  const roster = useMemo(() => {
    const dayData = grouped.find((r) => utcDayKey(r?.date) === selectedDay);
    const present = (dayData?.employees || [])
      .filter((e) => selectedShift === "all" || e?.records?.[0]?.shift === selectedShift)
      .map((e) => {
        const rec = e?.records?.[0] || {};
        const employee = empById[e.employeeId];
        const flags = flagsOf(rec, { dayKey: selectedDay, todayKey });
        return {
          employeeId: e.employeeId,
          employee,
          name: employee ? personName(employee) : "Unknown",
          rec,
          flags,
          status: rowStatus(flags),
        };
      });

    // Absence is only answerable for the whole org: with a shift picked, the
    // people without a record cannot be attributed to that shift, and calling
    // them absent from it would be an invention.
    const seen = new Set(present.map((p) => p.employeeId));
    const absent =
      selectedShift === "all"
        ? (allEmployees || [])
            .filter((e) => e?.isActive !== false && !seen.has(e._id))
            .map((e) => ({
              employeeId: e._id,
              employee: e,
              name: personName(e),
              rec: {},
              flags: flagsOf({}, { dayKey: selectedDay, todayKey }),
              status: rowStatus({}, { absent: true }),
            }))
        : [];

    return [...present, ...absent];
  }, [grouped, selectedDay, selectedShift, empById, allEmployees, todayKey]);

  const counts = useMemo(
    () => countBuckets(roster, { headcount: selectedShift === "all" ? totalActive : roster.length }),
    [roster, totalActive, selectedShift]
  );

  const rows = useMemo(() => selectRows(roster, { bucket, search }), [roster, bucket, search]);

  const inOffice = useMemo(
    () => roster.filter((e) => e.flags.present && fence && isPointInFence(fence, e.rec.arrivalLat, e.rec.arrivalLong)).length,
    [roster, fence]
  );

  // How much of this window's attendance was asserted rather than captured.
  const audit = useMemo(() => punchAudit(grouped), [grouped]);

  const trend = useMemo(
    () =>
      buildTrend({
        grouped,
        endKey: selectedDay,
        rangeDays,
        shift: selectedShift,
        headcountOn: headcountBasis.on,
        todayKey,
      }),
    [grouped, selectedDay, rangeDays, selectedShift, headcountBasis, todayKey]
  );

  const caption = trendCaption({
    rangeDays,
    endKey: selectedDay,
    daysWithData: trend?.daysWithData ?? null,
    headcountApproximate: headcountBasis.exact === null ? null : !headcountBasis.exact,
    corrected: audit.total > 0 ? audit.corrected : null,
    punches: audit.total,
  });

  const band = useMemo(() => arrivalBand(trend?.arrival), [trend]);

  // The arrival chart's right-hand axis label carries the reading itself —
  // "08:54 on 25 Aug" — and names the day it actually came from, which is not
  // always the day the page is anchored to.
  const arrivalPoints = useMemo(() => {
    if (!trend) return [];
    const i = trend.lastArrivalIndex;
    if (i < 0) return trend.points;
    return trend.points.map((p, idx) =>
      idx === trend.points.length - 1
        ? { ...p, label: `${clockLabel(trend.arrival[i])} on ${trend.points[i].label}` }
        : p
    );
  }, [trend]);

  const chartSeries = useMemo(
    () =>
      visibleSeries.map((k) => ({
        key: k,
        label: SERIES_META[k].label,
        color: SERIES_META[k].color,
        values: trend?.values?.[k] || [],
      })),
    [visibleSeries, trend]
  );

  const tableTitle = isToday ? "Today, by person" : `${dayLabel(selectedDay)}, by person`;

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading={"Attendance"} breadcrumbs={[{ label: "Attendance", href: "" }, { label: "Dashboard", href: "" }]} />

      <Box>
        {/* toolbar: the day everything on this screen is about, and the shift */}
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1.5} sx={{ mb: "1.25rem" }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CalendarTodayOutlinedIcon sx={{ fontSize: "1rem", color: tokens.textMuted }} />
            <Box component="input" type="date" value={selectedDay} max={todayKey}
              aria-label="Day"
              onChange={(e) => setSelectedDay(e.target.value)}
              sx={{ fontFamily: "inherit", fontSize: "0.875rem", fontWeight: 500, color: tokens.text,
                border: `1px solid ${tokens.borderStrong}`, borderRadius: "8px", px: 1.5, height: "2.75rem",
                background: tokens.card, outline: "none", "&:hover": { borderColor: tokens.textMuted }, "&:focus": { borderColor: tokens.primary, boxShadow: `inset 0 0 0 1px ${tokens.primary}` } }} />
            {!isToday && (
              <Box {...pressable(() => setSelectedDay(todayKey))}
                sx={{ fontSize: "0.8125rem", color: tokens.accent, cursor: "pointer", fontWeight: 600 }}>Today</Box>
            )}
          </Stack>
          <FilterSelect value={selectedShift} options={shiftOptions} onChange={(e) => setSelectedShift(e.target.value)} />
        </Stack>

        {/* six counters — each one a filter for the table below */}
        {/* One row, never two. It was a flex row set to wrap, which is the
            shape the book rules out: at some widths it ran six across, at
            others four and two. */}
        <Box sx={{ ...counterRowSx(COUNTERS.length), mb: "1.25rem" }}>
          {COUNTERS.map((c) => (
            <Counter
              key={c.bucket}
              label={!isToday && c.pastLabel ? c.pastLabel : c.label}
              value={counts[c.key]}
              tone={c.tone}
              active={bucket === c.bucket}
              onClick={() => setBucket(c.bucket)}
            />
          ))}
        </Box>

        {/* trend: the counters say what this day is, this says whether that's normal */}
        {trend && (
          <Box sx={{ ...panelSx, mb: "1.25rem" }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ md: "flex-start" }}
              spacing={1.5}
              sx={{ mb: "1.25rem" }}
            >
              <Box>
                <Box sx={panelTitleSx}>Attendance trend</Box>
                {/* The caption states the chart's own limitations. Attendance
                    data is always partly wrong, and a chart that hides that
                    gets trusted more than it deserves. */}
                <Box sx={{ ...captionSx, mt: 0.5 }}>{caption}</Box>
              </Box>
              <Stack direction="row" spacing={2.5} alignItems="flex-start">
                <Box sx={{ textAlign: "right" }}>
                  <Box sx={{ fontFamily: displayFont, fontSize: "1.625rem", fontWeight: 800, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}>
                    {trend.avg === null ? "—" : `${Math.round(trend.avg)}%`}
                  </Box>
                  <Box sx={captionSx}>period average</Box>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Stack direction="row" alignItems="baseline" spacing={0.75} justifyContent="flex-end">
                    <Box sx={{ fontFamily: displayFont, fontSize: "1.625rem", fontWeight: 800, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}>
                      {trend.selectedValue == null ? "—" : `${Math.round(trend.selectedValue)}%`}
                    </Box>
                    {trend.delta !== null && Math.abs(trend.delta) >= 1 && (
                      <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: trend.delta > 0 ? tokens.successText : tokens.warningText }}>
                        {trend.delta > 0 ? "▲" : "▼"}{Math.abs(Math.round(trend.delta))} pts
                      </Box>
                    )}
                  </Stack>
                  <Box sx={captionSx}>{isToday ? "today" : `on ${dayLabel(selectedDay)}`}</Box>
                </Box>

                {/* range is the chart's own control; the date above is the anchor */}
                <Stack direction="row" sx={{ border: `1px solid ${tokens.borderStrong}`, borderRadius: "999px", p: "3px", height: "fit-content" }}>
                  {[7, 30, 90].map((r) => (
                    <Box
                      key={r}
                      component="button"
                      type="button"
                      onClick={() => setRangeDays(r)}
                      aria-pressed={rangeDays === r}
                      sx={{
                        font: "inherit",
                        fontSize: "0.8125rem",
                        fontWeight: rangeDays === r ? 600 : 500,
                        border: "none",
                        cursor: "pointer",
                        borderRadius: "999px",
                        px: 1.75,
                        py: 0.75,
                        color: rangeDays === r ? tokens.onPrimary : tokens.textMuted,
                        background: rangeDays === r ? tokens.primary : "transparent",
                        "&:focus-visible": { outline: `2px solid ${tokens.accent}`, outlineOffset: "2px" },
                      }}
                    >
                      {r}d
                    </Box>
                  ))}
                </Stack>
              </Stack>
            </Stack>

            {/* The legend is the control. Three series are offered and at most
                two are drawn: picking a third drops whichever has been up
                longest, so the click always does something. */}
            <Stack direction="row" spacing={2.5} sx={{ mb: "1.125rem", flexWrap: "wrap", rowGap: 1 }}>
              {Object.entries(SERIES_META).map(([key, meta]) => {
                const on = visibleSeries.includes(key);
                return (
                  <Box
                    key={key}
                    component="button"
                    type="button"
                    aria-pressed={on}
                    onClick={() => setVisibleSeries((prev) => toggleSeries(prev, key))}
                    sx={{
                      font: "inherit",
                      background: "none",
                      border: "none",
                      p: 0,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      fontSize: "0.8125rem",
                      color: on ? tokens.text : tokens.textMuted,
                      fontWeight: on ? 600 : 500,
                      "&:focus-visible": { outline: `2px solid ${tokens.accent}`, outlineOffset: "3px" },
                    }}
                  >
                    <Box sx={{ width: "16px", height: "2px", borderRadius: "2px", background: on ? meta.color : tokens.borderStrong }} />
                    {meta.label}
                  </Box>
                );
              })}
            </Stack>

            {trend.daysWithData === 0 ? (
              <Box sx={{ py: "2rem", textAlign: "center", ...captionSx }}>
                No attendance was recorded in these {rangeDays} days.
              </Box>
            ) : (
              <TrendChart
                points={trend.points}
                series={chartSeries}
                height={180}
                markIndex={trend.points.length - 1}
                markLabel={isToday ? "today" : "selected"}
              />
            )}

            {/* Its own axis underneath, not a second scale on the plot above:
                minutes and percentages do not belong on one scale, and putting
                them there would draw a relationship the data never claimed. */}
            {trend.hasArrival && (
              <Box sx={{ mt: "1.5rem", pt: "1.375rem", borderTop: `1px solid ${tokens.border}` }}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: "1rem", gap: 1, flexWrap: "wrap" }}>
                  <Box sx={{ fontSize: "0.875rem", fontWeight: 600 }}>Average arrival time</Box>
                  <Box sx={captionSx}>Mean clock-in across everyone who came in</Box>
                </Stack>
                <TrendChart
                  points={arrivalPoints}
                  series={[{ key: "arrival", label: "Average arrival", color: chartColors[0], values: trend.arrival }]}
                  height={140}
                  yMin={band.min}
                  yMax={band.max}
                  formatValue={(v) => clockLabel(v)}
                  markIndex={trend.points.length - 1}
                  markLabel={isToday ? "today" : "selected"}
                />
              </Box>
            )}
          </Box>
        )}

        {/* body: the day's roster (hero) + the rail */}
        <Stack direction={{ xs: "column", lg: "row" }} spacing={2} alignItems="flex-start">
          <Box sx={{ ...panelSx, flex: 1.5, width: "100%", minWidth: 0 }}>
            <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={1.5} sx={{ mb: "1.125rem" }}>
              <Box sx={panelTitleSx}>{tableTitle}</Box>
              <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search employee" minWidth="12.5rem" />
            </Stack>

            {/* The chips are the counters again, carrying their counts. Same
                `bucket`, so selecting one here lights the tile above it. */}
            <Stack direction="row" spacing={1} sx={{ mb: "1.125rem", flexWrap: "wrap", rowGap: 1 }}>
              {CHIPS.map((c) => {
                const active = bucket === c.bucket;
                const n = c.key === null ? roster.length : counts[c.key];
                return (
                  <Box key={c.bucket} {...pressable(() => setBucket(c.bucket))}
                    aria-pressed={active}
                    sx={{
                      fontSize: "0.8125rem", fontWeight: active ? 600 : 500, cursor: "pointer",
                      px: "14px", py: "7px", borderRadius: "999px",
                      color: active ? tokens.onPrimary : tokens.textMuted,
                      background: active ? tokens.primary : "transparent",
                      border: `1px solid ${active ? tokens.primary : tokens.borderStrong}`,
                      "&:hover": { borderColor: active ? tokens.primary : tokens.textMuted },
                      "&:focus-visible": { outline: `2px solid ${tokens.accent}`, outlineOffset: "2px" },
                    }}>
                    {c.label} {n}
                  </Box>
                );
              })}
            </Stack>

            <DataTable
              rows={rows}
              loading={loading}
              error={failed}
              onRetry={() => setReloadKey((k) => k + 1)}
              errorTitle="Couldn't load this day"
              empty={{ title: "Nobody in this filter", description: "No one on the roster matches the selected counter for this day and shift." }}
              getRowId={(e) => e.employeeId}
              onRowClick={(e) => navigate(`/app/attendance-details/${e.employeeId}`)}
              tableSx={{ width: "100%", tableLayout: "fixed" }}
              columns={[
                {
                  key: "employee",
                  label: "Employee",
                  width: "40%",
                  render: (e) => (
                    <PersonCell
                      name={e.name}
                      secondary={e.employee?.designation || "—"}
                      src={e.employee?.profileImage}
                    />
                  ),
                },
                {
                  key: "status",
                  label: "Status",
                  width: "22%",
                  render: (e) => {
                    const t = toneColors(e.status.tone);
                    return (
                      <>
                        <StatusChip label={e.status.label} color={t.fg} bg={t.bg} />
                        {/* Same mark as the register, the strip, the muster and
                            the export: this day's punch was asserted by someone
                            rather than captured by the device. */}
                        {e.flags.corrected && (
                          <Box component="span" title="Punch corrected"
                            sx={{ ml: 0.5, fontSize: "0.875rem", fontWeight: 600, color: tokens.textMuted }}>*</Box>
                        )}
                      </>
                    );
                  },
                },
                {
                  key: "in",
                  label: "In",
                  align: "right",
                  cellSx: { fontVariantNumeric: "tabular-nums" },
                  render: (e) => (e.rec?.arrivalTime != null ? clockLabel(e.rec.arrivalTime) : <Dash />),
                },
                {
                  key: "out",
                  label: "Out",
                  align: "right",
                  cellSx: { fontVariantNumeric: "tabular-nums" },
                  render: (e) => (e.rec?.departureTime != null ? clockLabel(e.rec.departureTime) : <Dash />),
                },
                {
                  key: "hours",
                  label: "Hours",
                  align: "right",
                  cellSx: { fontVariantNumeric: "tabular-nums" },
                  render: (e) => {
                    const h = hoursLabel(e.rec);
                    return h === "—" ? <Dash /> : h;
                  },
                },
              ]}
            />

            {!loading && !failed && rows.length > 0 && (
              <Box sx={{ ...captionSx, mt: "1rem" }}>
                Showing {rows.length === roster.length ? `all ${roster.length}` : `${rows.length} of ${roster.length}`} people
              </Box>
            )}
          </Box>

          {/* rail */}
          <Stack spacing={2} sx={{ flex: 1, width: "100%", minWidth: 0 }}>
            <Box sx={panelSx}>
              <Box sx={{ ...panelTitleSx, mb: "1.25rem" }}>Breakdown</Box>
              <BreakdownBar label="On time" value={counts.onTime} total={totalActive} tone={tokens.success} />
              <BreakdownBar label="Late" value={counts.late} total={totalActive} tone={tokens.warning} />
              <BreakdownBar label="Missing punch" value={counts.missing} total={totalActive} tone={MISSING_PUNCH_COLOR} />
              <BreakdownBar label="Absent" value={counts.absent} total={totalActive} tone={tokens.danger} />
              {fence && (
                <Box sx={{ mt: "1rem", pt: "1rem", borderTop: `1px solid ${tokens.border}`, ...captionSx }}>
                  In office: <b style={{ color: tokens.text }}>{inOffice}</b> · Remote: <b style={{ color: tokens.text }}>{Math.max(counts.present - inOffice, 0)}</b>
                </Box>
              )}
            </Box>

            <Box sx={panelSx}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.125rem" }}>
                <Box sx={panelTitleSx}>Waiting on you</Box>
                <Box sx={linkBtnSx} {...pressable(() => navigate("/app/attendance-details/requests"))}>View all <ArrowForwardIcon sx={{ fontSize: "0.875rem" }} /></Box>
              </Stack>
              {requests.length === 0 ? (
                <Box sx={{ py: "1rem", textAlign: "center", ...captionSx }}>Nothing waiting on you</Box>
              ) : (
                <Stack spacing={0.5}>
                  {requests.slice(0, 6).map((r) => {
                    const emp = empById[r.employeeId];
                    const name = emp ? personName(emp) : "Unknown";
                    const date = r?.dayString ? dayLabel(r.dayString) : (r?.createdAt ? dayLabel(r.createdAt) : "—");
                    return (
                      <Stack key={r._id} direction="row" alignItems="center" spacing={1.25}
                        {...pressable(() => navigate("/app/attendance-details/requests"))}
                        sx={{ p: "0.5rem 0.25rem", borderRadius: "8px", cursor: "pointer", "&:hover": { background: tokens.surfaceSubtle } }}>
                        <Avatar sx={{ width: "2rem", height: "2rem", bgcolor: tokens.accent, fontSize: "0.6875rem", fontWeight: 600 }}>{initialsOf(name)}</Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.text }}>{name}</Box>
                          <Box sx={captionSx}>{date}</Box>
                        </Box>
                        <StatusChip label={TYPE_LABEL[r?.type] || r?.type} tone="warning" />
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};

export default AttendanceDashboard;
